/**
 * Guest List Preview
 *
 * Scrollable guest list with checkboxes for include/exclude toggling,
 * inline search, and download actions (CSV for Excel, EML for email clients).
 * Designed for the USI/HMC Events invitation workflow.
 *
 * Features:
 * - Search filter by name, email, or organization
 * - Select all / deselect all toggle
 * - Excluded guests shown with strikethrough styling
 * - Download guest list as Excel-compatible CSV
 * - Download invitation as .eml email file
 * - Optional "Send to Pipedrive" action button
 *
 * Used by:
 * - USI Events invitation step
 * - HMC Events invitation step
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Download,
  Mail,
  Send,
  Users,
  Check,
  X,
  Search,
} from 'lucide-react';
import {
  generateGuestListCsv,
  generateEmailFile,
  downloadBlob,
} from '../utils/exportUtils';
import type { ExportGuest } from '../utils/exportUtils';

// ============================================================
// TYPES
// ============================================================

interface Guest {
  personId: number;
  name: string;
  email: string;
  organization: string;
  title?: string | null;
}

interface GuestListPreviewProps {
  /** All guests (full list) */
  guests: Guest[];
  /** Currently excluded guest person IDs */
  excludedIds: Set<number>;
  /** Callback when exclusion set changes */
  onExcludedChange: (ids: Set<number>) => void;
  /** Event name for export filenames */
  eventName?: string;
  /** Email subject for EML export */
  emailSubject?: string;
  /** Email HTML body for EML export */
  emailHtml?: string;
  /** Whether to show the "Send to Pipedrive" button */
  showPipedriveButton?: boolean;
  /** Callback when "Send to Pipedrive" is clicked */
  onSendToPipedrive?: () => void;
  /** Optional className */
  className?: string;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function GuestListPreview({
  guests,
  excludedIds,
  onExcludedChange,
  eventName,
  emailSubject,
  emailHtml,
  showPipedriveButton = false,
  onSendToPipedrive,
  className = '',
}: GuestListPreviewProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // ---- Derived state ----

  const filteredGuests = useMemo(() => {
    if (!searchQuery.trim()) return guests;
    const q = searchQuery.toLowerCase();
    return guests.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        g.email.toLowerCase().includes(q) ||
        g.organization.toLowerCase().includes(q),
    );
  }, [guests, searchQuery]);

  const selectedCount = guests.length - excludedIds.size;
  const allSelected = excludedIds.size === 0;
  const noneSelected = excludedIds.size === guests.length;

  // ---- Handlers ----

  const handleToggleGuest = useCallback(
    (personId: number) => {
      const next = new Set(excludedIds);
      if (next.has(personId)) {
        next.delete(personId);
      } else {
        next.add(personId);
      }
      onExcludedChange(next);
    },
    [excludedIds, onExcludedChange],
  );

  const handleSelectAll = useCallback(() => {
    onExcludedChange(new Set());
  }, [onExcludedChange]);

  const handleDeselectAll = useCallback(() => {
    onExcludedChange(new Set(guests.map((g) => g.personId)));
  }, [guests, onExcludedChange]);

  const handleDownloadCsv = useCallback(() => {
    const included = guests.filter((g) => !excludedIds.has(g.personId));
    const exportData: ExportGuest[] = included.map((g) => ({
      name: g.name,
      email: g.email,
      organization: g.organization,
      title: g.title,
      personId: g.personId,
    }));
    const blob = generateGuestListCsv(exportData, eventName);
    const safeName = (eventName || 'guest-list').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadBlob(blob, `${safeName}.csv`);
  }, [guests, excludedIds, eventName]);

  const handleDownloadEml = useCallback(() => {
    if (!emailSubject || !emailHtml) return;
    const included = guests.filter((g) => !excludedIds.has(g.personId));
    const recipients = included.map((g) => ({ name: g.name, email: g.email }));
    const blob = generateEmailFile(emailSubject, emailHtml, recipients);
    const safeName = (eventName || 'invitation').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadBlob(blob, `${safeName}.eml`);
  }, [guests, excludedIds, emailSubject, emailHtml, eventName]);

  // ---- Render ----

  return (
    <div className={`rounded-xl border border-dark-border bg-dark-card ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-dark-muted" />
          <span className="text-sm font-medium text-dark-text">
            Guest List ({selectedCount} of {guests.length} selected)
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={allSelected}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-dark-text bg-dark-surface border border-dark-border/30 rounded-lg hover:bg-dark-input disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Check className="w-3 h-3" />
            Select All
          </button>
          <button
            type="button"
            onClick={handleDeselectAll}
            disabled={noneSelected}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-dark-text bg-dark-surface border border-dark-border/30 rounded-lg hover:bg-dark-input disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <X className="w-3 h-3" />
            Deselect All
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-2 border-b border-dark-border/50">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search guests by name, email, or org..."
            className="w-full pl-9 pr-3 py-1.5 text-sm bg-dark-surface border border-dark-border/30 rounded-lg text-dark-text placeholder:text-dark-muted focus:outline-none focus:ring-1 focus:ring-accent-blue/50 focus:border-accent-blue/50 transition-colors"
          />
        </div>
      </div>

      {/* Guest rows */}
      <div className="max-h-80 overflow-y-auto">
        {filteredGuests.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-dark-muted">
            {searchQuery ? 'No guests match your search.' : 'No guests to display.'}
          </div>
        ) : (
          filteredGuests.map((guest) => {
            const isExcluded = excludedIds.has(guest.personId);
            return (
              <label
                key={guest.personId}
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-dark-surface/50 transition-colors border-b border-dark-border/20 last:border-b-0 ${
                  isExcluded ? 'opacity-50' : ''
                }`}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={!isExcluded}
                  onChange={() => handleToggleGuest(guest.personId)}
                  className="w-4 h-4 rounded border-dark-border bg-dark-surface text-accent-blue focus:ring-accent-blue/50 focus:ring-offset-0 shrink-0"
                />

                {/* Name */}
                <span
                  className={`text-sm font-medium min-w-[140px] ${
                    isExcluded
                      ? 'line-through text-dark-muted'
                      : 'text-dark-text'
                  }`}
                >
                  {guest.name}
                </span>

                {/* Email (truncated) */}
                <span
                  className={`text-xs truncate max-w-[200px] ${
                    isExcluded
                      ? 'line-through text-dark-muted/60'
                      : 'text-dark-muted'
                  }`}
                  title={guest.email}
                >
                  {guest.email}
                </span>

                {/* Organization */}
                <span
                  className={`text-xs truncate max-w-[160px] ml-auto ${
                    isExcluded
                      ? 'line-through text-dark-muted/60'
                      : 'text-dark-muted'
                  }`}
                  title={guest.organization}
                >
                  {guest.organization}
                </span>
              </label>
            );
          })
        )}
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-t border-dark-border bg-dark-bg/50 rounded-b-xl">
        <button
          type="button"
          onClick={handleDownloadCsv}
          disabled={selectedCount === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/80 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Download Excel
        </button>

        {emailSubject && emailHtml && (
          <button
            type="button"
            onClick={handleDownloadEml}
            disabled={selectedCount === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/80 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
            Download EML
          </button>
        )}

        {showPipedriveButton && onSendToPipedrive && (
          <button
            type="button"
            onClick={onSendToPipedrive}
            disabled={selectedCount === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-600/80 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors ml-auto"
          >
            <Send className="w-3.5 h-3.5" />
            Send to Pipedrive
          </button>
        )}
      </div>
    </div>
  );
}

export default GuestListPreview;
